<template>
  <transition name="slide-fade">
    <div class="job-edit-modal edit-frame-selections-modal" v-if="value">
      <div class="title-bar top" style="flex-direction: column">
        <div style="display: flex; width: 100%; justify-content: space-between">
          <h4>
            {{ order.vend_invoice_number }}
            <span v-if="customerName"> — {{ customerName }}</span>
          </h4>
          <a class="close-modal" @click="close"><i class="di-remove-10"></i></a>
        </div>
        <div class="name-bar">
          <div>
            <h2 style="text-align: left">{{ productName }}</h2>
            <div class="description">{{ productOptions }}</div>
          </div>
        </div>
      </div>

      <div class="modal-wrapper">
        <ul class="text-danger" v-if="errors.length">
          <li v-for="(error, index) in errors" :key="index">{{ error }}</li>
        </ul>

        <div class="step-holder">
          <div class="title-bar">
            <h3><span>Edit Selections</span></h3>
          </div>

          <div class="table-title-wrapper">
            <h5>Size</h5>
          </div>
          <div class="selection-table">
            <div class="selection-item" v-if="showShape">
              <h6>Shape</h6>
              <div class="selection-holder">
                <div class="radio-buttons">
                  <label
                    v-for="shape in frameShapes"
                    :key="shape.value"
                    class="radio-button"
                  >
                    <input
                      type="radio"
                      :value="shape.value"
                      v-model="form.shape"
                      name="frame-shape"
                    />
                    <span class="radio-button-content">
                      <span class="radio-circle"></span>
                      <img :src="shape.image" :alt="shape.text" class="shape-icon" />
                      {{ shape.text }}
                    </span>
                  </label>
                </div>
              </div>
            </div>

            <div class="selection-type" v-if="showFront || showArms">
              <h6>Size</h6>
              <div class="options-holder">
                <div
                  v-for="size in sizeOptionsList"
                  :key="`set-${size.value}`"
                  class="single-option"
                >
                  <div
                    class="option-label"
                    :class="{ disabled: size.disabled }"
                    @click="!size.disabled && setAllSizes(size.value)"
                  >
                    {{ size.text }}
                  </div>
                </div>
              </div>
            </div>

            <div class="selection-item" v-if="showFront">
              <h6>Frame</h6>
              <div class="selection-holder">
                <b-form-group>
                  <b-form-radio-group
                    v-model="form.front.size"
                    :options="frontSizeOptions"
                  ></b-form-radio-group>
                </b-form-group>
              </div>
            </div>

            <div class="selection-item" v-if="showArms">
              <h6>Right Arm</h6>
              <div class="selection-holder">
                <b-form-group>
                  <b-form-radio-group
                    v-model="form.right.size"
                    :options="armSizeOptions"
                  ></b-form-radio-group>
                </b-form-group>
              </div>
            </div>

            <div class="selection-item" v-if="showArms">
              <h6>Left Arm</h6>
              <div class="selection-holder">
                <b-form-group>
                  <b-form-radio-group
                    v-model="form.left.size"
                    :options="armSizeOptions"
                  ></b-form-radio-group>
                </b-form-group>
              </div>
            </div>
          </div>

          <div v-if="showColours">
            <div class="table-title-wrapper">
              <h5>Colour</h5>
            </div>
            <div class="selection-table colour-table">
              <div class="selection-type">
                <h6>Colour</h6>
                <div class="options-holder">
                  <div v-for="row in colourRows" :key="`set-${row.name}`" class="single-option">
                    <div class="option-label" @click="setAllColours(row)">
                      <span class="color" :style="{ backgroundColor: row.hex_code }"></span>
                      {{ row.name }}
                    </div>
                  </div>
                </div>
              </div>

              <div class="selection-item" v-if="showFront && form.front.size !== 'nd'">
                <h6>Frame</h6>
                <div class="selection-holder">
                  <b-form-group>
                    <b-form-radio-group
                      v-model="form.front.colorId"
                      :options="frameColorOptions"
                      value-field="id"
                      text-field="name"
                    ></b-form-radio-group>
                  </b-form-group>
                </div>
              </div>

              <div class="selection-item" v-if="showArms && form.right.size !== 'nd'">
                <h6>Right Arm</h6>
                <div class="selection-holder">
                  <b-form-group>
                    <b-form-radio-group
                      v-model="form.right.colorId"
                      :options="armColorOptions"
                      value-field="id"
                      text-field="name"
                    ></b-form-radio-group>
                  </b-form-group>
                </div>
              </div>

              <div class="selection-item" v-if="showArms && form.left.size !== 'nd'">
                <h6>Left Arm</h6>
                <div class="selection-holder">
                  <b-form-group>
                    <b-form-radio-group
                      v-model="form.left.colorId"
                      :options="armColorOptions"
                      value-field="id"
                      text-field="name"
                    ></b-form-radio-group>
                  </b-form-group>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="footer-bar">
          <b-button class="mr-3" variant="outline-secondary" @click="close" type="button">
            Cancel
          </b-button>
          <b-button
            class="button-with-loader"
            variant="primary"
            type="button"
            :disabled="isLoading"
            @click="submit"
          >
            <b-spinner v-if="isLoading" small />
            <span v-else>Save and close</span>
          </b-button>
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
import { mapState } from 'vuex';
import apiOrders from '@/api/orders';
import { SIZES } from '@/support/constants';

const FRAME_PART_KEYS = {
  set: ['frame-set'],
  front: ['extra-frame', 'le-extra-frame'],
  arms: ['extra-temples', 'le-extra-temples'],
};

const GLASSES_TYPE_KEYS = new Set([
  'glasses',
  'lenses-only',
  'single-vision',
  'multifocal',
  'bifocal',
  'trifocal',
  'desk',
  'dresdengo',
  'dg-reading-glass',
]);

function resolveFramePartType(item) {
  let type = item?.region_product?.product?.type;
  while (type) {
    if (GLASSES_TYPE_KEYS.has(type.key)) return null;
    if (FRAME_PART_KEYS.set.includes(type.key)) return 'set';
    if (FRAME_PART_KEYS.front.includes(type.key)) return 'front';
    if (FRAME_PART_KEYS.arms.includes(type.key)) return 'arms';
    type = type.parent_type;
  }

  const attr = item?.region_product?.product?.attribute;
  if (attr) {
    if (attr.arm_size || attr.arm_colour_id) return 'arms';
    if (attr.frame_size || attr.frame_colour_id) return 'front';
  }

  const name = (item?.region_product?.product?.name || '').trim().toLowerCase();
  if (/warranty\s*claim\s*\/\s*frame\s*set/.test(name)) return 'set';
  if (/warranty\s*claim\s*\/\s*arms?\b/.test(name)) return 'arms';
  if (/warranty\s*claim\s*\/\s*frame\b/.test(name)) return 'front';
  if (/^frame\s*parts\s*\/\s*arms?\b/.test(name)) return 'arms';
  if (/^frame\s*parts\s*\/\s*frame\b/.test(name)) return 'front';

  const component = item?.additional_info?.data?.customize?.component;
  if (component) {
    const hasFrame = !!component.Frame;
    const hasTemple = !!(component['Temple Left'] || component['Temple Right']);
    if (hasFrame && hasTemple) return 'set';
    if (hasFrame) return 'front';
    if (hasTemple) return 'arms';
  }

  return null;
}

function sizeToAbbr(value) {
  if (!value) return null;
  const v = String(value).toLowerCase();
  if (v === 'l' || v === 'large') return 'l';
  if (v === 'm' || v === 'medium') return 'm';
  if (v === 's' || v === 'small') return 's';
  if (v === 'xs' || v === 'extra-small') return 'extra-small';
  if (v === 'nd' || v === 'non-dresden') return 'nd';
  return null;
}

export default {
  name: 'EditFrameSelectionsModal',
  props: {
    value: { type: Boolean, default: false },
    order: { type: Object, required: true },
    item: { type: Object, required: true },
  },
  data() {
    return {
      form: {
        shape: null,
        front: { size: null, colorId: null },
        left: { size: null, colorId: null },
        right: { size: null, colorId: null },
      },
      errors: [],
      isLoading: false,
      frameShapes: [
        {
          value: 'original',
          text: 'Original',
          image: 'https://w.dresden.vision/images/icons/shape_classic.png',
        },
        {
          value: 'capote',
          text: 'Capote',
          image: 'https://w.dresden.vision/images/icons/shape_round.png',
        },
      ],
    };
  },
  computed: {
    ...mapState({
      colors: state => state.jobsModule.colors,
    }),
    framePartType() {
      return resolveFramePartType(this.item);
    },
    showShape() {
      return ['set', 'front'].includes(this.framePartType);
    },
    showFront() {
      return ['set', 'front'].includes(this.framePartType);
    },
    showArms() {
      return ['set', 'arms'].includes(this.framePartType);
    },
    showColours() {
      if (this.showFront && this.form.front.size && this.form.front.size !== 'nd') return true;
      if (this.showArms && this.form.right.size && this.form.right.size !== 'nd') return true;
      if (this.showArms && this.form.left.size && this.form.left.size !== 'nd') return true;
      return false;
    },
    sizeOptionsList() {
      return SIZES.filter(s => s.value !== 'nd').map(s => ({
        ...s,
        disabled: this.showFront && s.value === 'extra-small' && this.form.shape === 'capote',
      }));
    },
    frontSizeOptions() {
      return SIZES.filter(s => s.value !== 'nd').map(s => ({
        ...s,
        disabled: s.value === 'extra-small' && this.form.shape === 'capote',
      }));
    },
    armSizeOptions() {
      return SIZES.filter(s => s.value !== 'nd');
    },
    frameColors() {
      return this.filterColors('Frame');
    },
    armColors() {
      return this.filterColors('Temple');
    },
    // The frame and arm radios are separate columns next to the colour names, so every
    // column needs exactly one entry per row or the dots end up beside the wrong name.
    colourRows() {
      const names = this._.sortBy(
        this._.uniq([...this.frameColors, ...this.armColors].map(c => c.name)),
      );
      return names.map(name => {
        const frame = this.frameColors.find(c => c.name === name);
        const arm = this.armColors.find(c => c.name === name);
        return { name, hex_code: (frame || arm).hex_code, frame, arm };
      });
    },
    frameColorOptions() {
      return this.colourRows.map(row => this.colourOption(row.frame, row.name));
    },
    armColorOptions() {
      return this.colourRows.map(row => this.colourOption(row.arm, row.name));
    },
    additionalInfoId() {
      return this._.get(this.item, 'additional_info.id');
    },
    productName() {
      const name = this._.get(this.item, 'region_product.product.name', '');
      const [first] = name.split('/');
      return (first || name).trim();
    },
    productOptions() {
      const name = this._.get(this.item, 'region_product.product.name', '');
      const parts = name.split('/');
      return parts.slice(1).join('/').trim();
    },
    customerName() {
      const c = this._.get(this.order, 'customer');
      if (!c) return '';
      return [c.first_name, c.last_name].filter(Boolean).join(' ');
    },
  },
  watch: {
    value(open) {
      if (open) this.onShow();
    },
  },
  methods: {
    filterColors(type) {
      const filtered = this._.filter(
        this.colors || [],
        c => c.type === type && c.visible_online === 1 && c.name !== 'Existing Dresden',
      );
      return this._.sortBy(filtered, ['name']);
    },
    setAllSizes(value) {
      if (this.showFront) this.form.front.size = value;
      if (this.showArms) {
        this.form.left.size = value;
        this.form.right.size = value;
      }
    },
    colourOption(color, name) {
      return color ? { id: color.id, name } : { id: `none-${name}`, name, disabled: true };
    },
    setAllColours(row) {
      if (this.showFront && row.frame) this.form.front.colorId = row.frame.id;
      if (this.showArms && row.arm) {
        this.form.left.colorId = row.arm.id;
        this.form.right.colorId = row.arm.id;
      }
    },
    onShow() {
      this.errors = [];
      this.isLoading = false;
      if (!this.colors) {
        this.$store.commit('jobsModule/GET_ALL_COLORS');
      }
      const component = this._.get(this.item, 'additional_info.data.customize.component', {});
      const frame = component.Frame || {};
      const tLeft = component['Temple Left'] || {};
      const tRight = component['Temple Right'] || {};
      this.form.shape = frame.shape || null;
      this.form.front.size = sizeToAbbr(frame.sizeId);
      this.form.front.colorId = this._.get(frame, 'color.id') || null;
      this.form.left.size = sizeToAbbr(tLeft.sizeId);
      this.form.left.colorId = this._.get(tLeft, 'color.id') || null;
      this.form.right.size = sizeToAbbr(tRight.sizeId);
      this.form.right.colorId = this._.get(tRight, 'color.id') || null;

      const attr = this._.get(this.item, 'region_product.product.attribute');
      if (attr && !Object.keys(component).length) {
        if (this.showFront) {
          if (!this.form.front.size) this.form.front.size = sizeToAbbr(attr.frame_size);
          if (!this.form.front.colorId) this.form.front.colorId = attr.frame_colour_id || null;
        }
        if (this.showArms) {
          const armSize = sizeToAbbr(attr.arm_size);
          const armColorId = attr.arm_colour_id || null;
          if (!this.form.left.size) this.form.left.size = armSize;
          if (!this.form.left.colorId) this.form.left.colorId = armColorId;
          if (!this.form.right.size) this.form.right.size = armSize;
          if (!this.form.right.colorId) this.form.right.colorId = armColorId;
        }
      }
    },
    close() {
      this.$emit('input', false);
    },
    buildPayload() {
      const customize = {
        front: { size: 'nd' },
        left: { size: 'nd' },
        right: { size: 'nd' },
      };
      if (this.showFront) {
        customize.front.size = this.form.front.size || 'nd';
        if (this.form.front.colorId) customize.front.colorId = this.form.front.colorId;
        if (this.showShape && this.form.shape) customize.front.shape = this.form.shape;
      }
      if (this.showArms) {
        customize.left.size = this.form.left.size || 'nd';
        if (this.form.left.colorId) customize.left.colorId = this.form.left.colorId;
        customize.right.size = this.form.right.size || 'nd';
        if (this.form.right.colorId) customize.right.colorId = this.form.right.colorId;
      }
      if (this.showFront && !this.showArms && customize.front.colorId && !customize.right.colorId) {
        customize.right.colorId = customize.front.colorId;
        customize.left.colorId = customize.front.colorId;
      }
      return { customize };
    },
    async submit() {
      this.isLoading = true;
      this.errors = [];
      try {
        const payload = this.buildPayload();
        if (this.additionalInfoId) {
          await apiOrders.updateOrderItemAdditionalInfo(
            this.item.id,
            this.additionalInfoId,
            payload,
          );
        } else {
          await apiOrders.createOrderItemAdditionalInfo(this.item.id, payload);
        }
        this.$store.commit('SET_EXPAND_ITEM_ID', this.item.id);
        this.$root.$emit('reloadExpandedJob', this.order.id);
        this.$store.dispatch('showSuccessAlert', 'Selections Updated');
        this.close();
      } catch (error) {
        this.errors = this._.get(error, 'response.data.errors')
          || [this._.get(error, 'response.data.message', 'Update failed')];
      } finally {
        this.isLoading = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.radio-buttons {
  display: flex;
  flex-direction: column;
  width: 275px;
  gap: 10px;
}

.radio-button {
  flex: 1;
  min-width: 80px;
  position: relative;
  cursor: pointer;
}

.radio-button input {
  position: absolute;
  opacity: 0;
  cursor: pointer;
}

.radio-button-content {
  display: flex;
  align-items: center;
  padding: 10px;
  border-radius: 5px;
  transition: all 0.3s ease;
}

.radio-circle {
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background-color: #fff;
  border: 1px solid #dedede;
  margin-right: 10px;
  display: inline-block;
  transition: all 0.3s ease;
}

.radio-button input:checked + .radio-button-content .radio-circle {
  background-color: #007bff;
  box-shadow: 0 0 0 2px #ffffff, 0 0 0 4px #007bff;
}

.shape-icon {
  width: 70px;
  height: 24px;
  margin-right: 8px;
  object-fit: contain;
}

.radio-button:hover .radio-button-content {
  border-color: #007bff;
}

::v-deep .option-label.disabled {
  opacity: 0.4;
  pointer-events: none;
}
</style>

<style lang="scss">
.edit-frame-selections-modal {
  display: block !important;
  padding: 0 !important;
  background-color: #f4f5f7 !important;
  left: 0 !important;
  right: 0 !important;
  top: 0 !important;
  bottom: 0 !important;
  width: 100vw !important;
}
.edit-frame-selections-modal .modal-wrapper {
  width: 100% !important;
  padding: 24px 40px 96px !important;
  position: fixed !important;
  top: 130px !important;
  bottom: 60px !important;
  left: 0 !important;
  right: 0 !important;
  margin: 0 !important;
}
.edit-frame-selections-modal .title-bar.top {
  width: 100% !important;
  left: 0 !important;
  right: 0 !important;
  top: 0 !important;
  padding: 18px 40px !important;
}
.edit-frame-selections-modal .footer-bar {
  width: 100% !important;
  left: 0 !important;
  right: 0 !important;
  bottom: 0 !important;
  padding: 18px 40px !important;
}
</style>
